import { computed, ref, watch } from 'vue'
import useUserStore from '@/store/modules/user'
import { getOrganizationLessonBalances, getOrgComputeInfo } from '@/api/organization'

// orgId comes from getInfo, not from a menu title or a URL parameter.
export function isInstitutionUser() {
  const id = useUserStore().orgId
  return id !== undefined && id !== null && String(id) !== '' && String(id) !== '0'
}
export function canViewInstitutionAssets() {
  const user = useUserStore()
  return isInstitutionUser() && (user.permissions.includes('*:*:*') || user.permissions.includes('org:hour:list'))
}
export function assetResponseData(response: any) {
  if (response?.code !== undefined && response.code !== 200) throw new Error('Asset request failed')
  return response?.data ?? response
}

// Keep the existing batch-based formula, including its non-negative consumed value.
export function summarizeLessonBalances(rows: any[]) {
  const granted = rows.reduce((sum, row) => sum + Number(row.changeAmount || 0), 0)
  const remaining = rows.reduce((sum, row) => sum + Number(row.remaining || 0), 0)
  return { granted, remaining, consumed: Math.max(0, granted - remaining) }
}

export function useInstitutionAssets() {
  const user = useUserStore()
  const allowed = computed(canViewInstitutionAssets)
  const organizationId = computed(() => allowed.value ? user.orgId : undefined)
  function resource<T>(fetchData: (id: any) => Promise<T>) {
    const data = ref<T | null>(null)
    const loading = ref(false)
    const error = ref(false)
    let requestId = 0
    async function load() {
      const current = ++requestId
      const id = organizationId.value
      data.value = null
      error.value = false
      loading.value = false
      if (!id) return
      loading.value = true
      try {
        const result = await fetchData(id)
        if (current === requestId && id === organizationId.value) data.value = result as any
      } catch (_) {
        if (current === requestId) error.value = true
      } finally {
        if (current === requestId) loading.value = false
      }
    }
    watch(organizationId, () => { ++requestId; data.value = null; error.value = false; loading.value = false })
    return { data, loading, error, load }
  }
  const lessons = resource(async id => {
    const rows = assetResponseData(await getOrganizationLessonBalances(id, true))
    if (!Array.isArray(rows)) throw new Error('Invalid lesson balances')
    return { rows, ...summarizeLessonBalances(rows) }
  })
  const compute = resource(async id => {
    const data = assetResponseData(await getOrgComputeInfo(id))
    if (!data || data.remain == null || data.consume == null) throw new Error('Missing compute summary')
    // Preserve negative balances and the original time fields without inferring their meaning.
    return data
  })
  return { allowed, organizationId, lessons, compute }
}
